/**
 * FmSurface — a surface that owns its polarity inside an archetype of the other
 * polarity. `glass-deep` puts the deep statement tone on translucent glass over
 * a light page or map: the surface element takes the dark theme through
 * FmThemeIsland and declares the band, figure and surface roles on itself
 * (tokens.css), because a var() chain resolves where it is declared. Copy, key
 * figures, ranked lists, actions, notices and FmSelector inside it render deep
 * without any route or component `data-theme`.
 */
import React from 'react';
import {
  FmSurfaceContext,
  validateSurface,
  type FmSurfaceKind,
  type FmSurfacePadding,
} from './surfaceContract';
import { FmThemeIsland } from '@/shared/ui/FmThemeProvider';
import './FmSurface.css';

export interface FmSurfaceProps {
  surface: FmSurfaceKind;
  /** Landmark the surface renders as; a side panel is an `aside`. */
  as?: 'div' | 'section' | 'aside';
  padding?: FmSurfacePadding;
  /** Accessible name; required for a `section` or `aside` landmark. */
  'aria-label'?: string;
  children: React.ReactNode;
  'data-testid'?: string;
  /**
   * @internal Placement hook for the design-system consumer that positions the
   * surface (an overlay corner, an inspector rail); forbidden in route code.
   */
  className?: string;
}

export const FmSurface: React.FC<FmSurfaceProps> = ({
  surface,
  as: Landmark = 'div',
  padding = 'comfortable',
  'aria-label': ariaLabel,
  children,
  'data-testid': testId,
  className,
}) => {
  validateSurface(surface, padding);
  if (Landmark !== 'div' && !ariaLabel) {
    throw new Error(
      `FmSurface: an "${Landmark}" surface needs an aria-label to name its landmark.`,
    );
  }
  return (
    <FmSurfaceContext.Provider value={surface}>
      <FmThemeIsland theme="dark">
        <Landmark
          className={['fm-surface', `fm-surface--${surface}`, className].filter(Boolean).join(' ')}
          data-surface={surface}
          data-padding={padding}
          aria-label={ariaLabel}
          data-testid={testId}
        >
          {children}
        </Landmark>
      </FmThemeIsland>
    </FmSurfaceContext.Provider>
  );
};

FmSurface.displayName = 'FmSurface';
